import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Report, ReportType } from "@/core";
import {
  formatCurrency,
  formatNumber,
  formatPercentage,
} from "@/utils/formatting";
import {
  Column,
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  VisibilityState,
} from "@tanstack/react-table";
import { format, isValid } from "date-fns";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  BarChart3,
  Calendar,
  Package,
  Settings2,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

// Helper function to safely format dates
function safeFormatDate(dateValue: unknown, formatString: string): string {
  if (!dateValue) return "-";

  const date = new Date(dateValue as string | number | Date);
  if (!isValid(date)) return "-";

  return format(date, formatString);
}

// Define which columns should be treated as numeric for alignment purposes
const NUMERIC_COLUMNS = new Set<string>([
  "totalRevenue",
  "averageDealValue",
  "winRate",
  "totalDeals",
  "wonDeals",
  "lostDeals",
  "numberOfPendingDeals",
  "totalQuantity",
  "lostRevenue",
  "potentialRevenue",
  "pendingPercentageOfDeals",
  "wonPercentageOfTotal",
  "lostPercentageOfTotal",
  "boughtMiles",
  "soldMiles",
  "topProductRevenue",
]);

function isNumericColumn(columnId?: string): boolean {
  if (!columnId) return false;
  return NUMERIC_COLUMNS.has(columnId);
}

interface ReportTableProps {
  reports: Report[];
  isLoading?: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
  selectedReportType: ReportType | "all";
  onSortingChange?: (sorting: SortingState) => void;
}
function SortableHeader({
  column,
  children,
}: {
  column: Column<Report, unknown>;
  children: React.ReactNode;
}) {
  return (
    <Button
      variant="ghost"
      onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      className="h-auto p-0 font-semibold hover:bg-transparent"
    >
      {children}
      {column.getIsSorted() === "asc" ? (
        <ArrowUp className="ml-2 h-4 w-4" />
      ) : column.getIsSorted() === "desc" ? (
        <ArrowDown className="ml-2 h-4 w-4" />
      ) : (
        <ArrowUpDown className="ml-2 h-4 w-4" />
      )}
    </Button>
  );
}

export function ReportTable({
  reports,
  isLoading = false,
  selectedReportType = "all",
  onSortingChange,
}: ReportTableProps) {
  const navigate = useNavigate();
  const [sorting, setSorting] = useState<SortingState>([
    { id: "productName", desc: false },
  ]);
  useEffect(() => {
    if (onSortingChange) {
      onSortingChange(sorting);
    }
  }, [sorting, onSortingChange]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({
    id: false,
  });

  // Update column visibility and set default sorting when selectedReportType changes
  useEffect(() => {
    setColumnVisibility((prev) => ({
      ...prev,
      productName:
        selectedReportType === ReportType.PRODUCT ||
        selectedReportType === "all",
      period: selectedReportType === ReportType.MONTHLY,
      uniqueProducts:
        selectedReportType === ReportType.MONTHLY ||
        selectedReportType === "all",
      uniqueCustomers:
        selectedReportType === ReportType.MONTHLY ||
        selectedReportType === "all",
      topProductName:
        selectedReportType === ReportType.MONTHLY ||
        selectedReportType === "all",
      topProductRevenue:
        selectedReportType === ReportType.MONTHLY ||
        selectedReportType === "all",
    }));

    if (selectedReportType === ReportType.PRODUCT) {
      setSorting([{ id: "productName", desc: false }]);
    } else if (selectedReportType === ReportType.MONTHLY) {
      setSorting([{ id: "period", desc: true }]);
    } else {
      setSorting([{ id: "productName", desc: false }]);
    }
  }, [selectedReportType]);

  const columns = useMemo<ColumnDef<Report>[]>(
    () => [
      {
        accessorKey: "id",
        header: "ID",
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.getValue("id")}
          </span>
        ),
      },
      {
        accessorKey: "productName",
        header: ({ column }) => (
          <SortableHeader column={column}>Product</SortableHeader>
        ),
        cell: ({ row }) => {
          const report = row.original;
          if (report.type === ReportType.PRODUCT && "productName" in report) {
            return (
              <div className="flex items-center justify-start gap-2">
                <Package className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">{report.productName}</span>
              </div>
            );
          }
          return <span className="text-muted-foreground">-</span>;
        },
      },
      {
        accessorKey: "period",
        header: ({ column }) => (
          <SortableHeader column={column}>Period</SortableHeader>
        ),
        cell: ({ row }) => {
          const report = row.original;
          if (
            report.type === ReportType.MONTHLY &&
            "month" in report &&
            "year" in report
          ) {
            return (
              <div className="flex items-center justify-start gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">
                  {new Date(report.year, report.month - 1).toLocaleDateString(
                    "en-US",
                    {
                      month: "long",
                      year: "numeric",
                    },
                  )}
                </span>
              </div>
            );
          }
          return <span className="text-muted-foreground">-</span>;
        },
      },
      {
        accessorKey: "totalRevenue",
        header: ({ column }) => (
          <SortableHeader column={column}>Revenue</SortableHeader>
        ),
        cell: ({ row }) => {
          const report = row.original;
          const totalRevenue = report.totalRevenue || 0;
          return (
            <div className="flex items-center justify-end gap-2">
              <span className="font-semibold text-green-600 ">
                {formatCurrency(totalRevenue)}
              </span>
            </div>
          );
        },
      },
      {
        accessorKey: "averageDealValue",
        header: ({ column }) => (
          <SortableHeader column={column}>Avg Deal</SortableHeader>
        ),
        cell: ({ row }) => {
          const report = row.original;
          const avgDeal = report.averageDealValue || 0;
          return (
            <div className="flex items-center justify-end gap-2">
              <span className="font-semibold text-blue-600">
                {formatCurrency(avgDeal)}
              </span>
            </div>
          );
        },
      },
      {
        accessorKey: "winRate",
        header: ({ column }) => (
          <SortableHeader column={column}>Win Rate</SortableHeader>
        ),
        cell: ({ row }) => {
          const report = row.original;
          const winRate = report.winRate || 0;
          return (
            <div className="flex items-center justify-end gap-2">
              {formatPercentage(winRate * 100)}
            </div>
          );
        },
      },
      {
        accessorKey: "totalDeals",
        header: ({ column }) => (
          <SortableHeader column={column}>Total Deals</SortableHeader>
        ),
        cell: ({ row }) => {
          const report = row.original;
          const totalDeals = report.totalDeals || 
            (report.wonDeals || 0) + (report.lostDeals || 0) + (report.numberOfPendingDeals || 0);
          return (
            <span className="font-medium">{formatNumber(totalDeals)}</span>
          );
        },
      },
      {
        accessorKey: "wonDeals",
        header: ({ column }) => (
          <SortableHeader column={column}>Won Deals</SortableHeader>
        ),
        cell: ({ row }) => {
          const report = row.original;
          const wonDeals = report.wonDeals || 0;
          return (
            <div className="flex items-center justify-end gap-2">
              <span className="font-medium text-green-600">
                {formatNumber(wonDeals)}
              </span>
            </div>
          );
        },
      },
      {
        accessorKey: "lostDeals",
        header: ({ column }) => (
          <SortableHeader column={column}>Lost Deals</SortableHeader>
        ),
        cell: ({ row }) => {
          const report = row.original;
          const lostDeals = report.lostDeals || 0;
          return (
            <div className="flex items-center justify-end gap-2">
              <span className="font-medium text-red-600">
                {formatNumber(lostDeals)}
              </span>
            </div>
          );
        },
      },
      {
        accessorKey: "numberOfPendingDeals",
        header: ({ column }) => (
          <SortableHeader column={column}>Pending Deals</SortableHeader>
        ),
        cell: ({ row }) => {
          const report = row.original;
          return (
            <div className="flex items-center justify-end gap-2">
              <span className="font-medium text-yellow-600">
                {formatNumber(report.numberOfPendingDeals || 0)}
              </span>
            </div>
          );
        },
      },
      {
        accessorKey: "totalQuantity",
        header: ({ column }) => (
          <SortableHeader column={column}>Total Quantity</SortableHeader>
        ),
        cell: ({ row }) => {
          const report = row.original;
          return (
            <span className="font-medium">
              {formatNumber(report.totalQuantity || 0)}
            </span>
          );
        },
      },
      {
        accessorKey: "lostRevenue",
        header: ({ column }) => (
          <SortableHeader column={column}>Lost Revenue</SortableHeader>
        ),
        cell: ({ row }) => {
          const report = row.original;
          return (
            <div className="flex items-center justify-end gap-2">
              <span className="font-semibold text-red-600">
                {formatCurrency(report.lostRevenue || 0)}
              </span>
            </div>
          );
        },
      },
      {
        accessorKey: "potentialRevenue",
        header: ({ column }) => (
          <SortableHeader column={column}>Potential Revenue</SortableHeader>
        ),
        cell: ({ row }) => {
          const report = row.original;
          return (
            <div className="flex items-center justify-end gap-2">
              <span className="font-semibold text-purple-600">
                {formatCurrency(report.potentialRevenue || 0)}
              </span>
            </div>
          );
        },
      },
      {
        accessorKey: "pendingPercentageOfDeals",
        header: ({ column }) => (
          <SortableHeader column={column}>Pending % of Deals</SortableHeader>
        ),
        cell: ({ row }) => {
          const report = row.original;
          return (
            <div className="flex items-center justify-end gap-2">
              {formatPercentage((report.pendingPercentageOfDeals || 0) * 100)}
            </div>
          );
        },
      },
      {
        accessorKey: "wonPercentageOfTotal",
        header: ({ column }) => (
          <SortableHeader column={column}>Won % of Deals</SortableHeader>
        ),
        cell: ({ row }) => {
          const report = row.original;
          return (
            <div className="flex items-center justify-end gap-2">
              {formatPercentage((report.wonPercentageOfTotal || 0) * 100)}
            </div>
          );
        },
      },
      {
        accessorKey: "lostPercentageOfTotal",
        header: ({ column }) => (
          <SortableHeader column={column}>Lost % of Deals</SortableHeader>
        ),
        cell: ({ row }) => {
          const report = row.original;
          return (
            <div className="flex items-center justify-end gap-2">
              {formatPercentage((report.lostPercentageOfTotal || 0) * 100)}
            </div>
          );
        },
      },
      {
        accessorKey: "rejectionRate",
        header: ({ column }) => (
          <SortableHeader column={column}>Rejection Rate</SortableHeader>
        ),
        cell: ({ row }) => {
          const report = row.original;
          return (
            <div className="flex items-center justify-end gap-2">
              {formatPercentage((report.rejectionRate || 0) * 100)}
            </div>
          );
        },
      },
      {
        accessorKey: "boughtMiles",
        header: ({ column }) => (
          <SortableHeader column={column}>Bought Miles</SortableHeader>
        ),
        cell: ({ row }) => {
          const report = row.original;
          if (
            (report.type === ReportType.PRODUCT &&
              "boughtMiles" in report &&
              report.boughtMiles !== undefined) ||
            (report.type === ReportType.MONTHLY &&
              "boughtMiles" in report &&
              report.boughtMiles !== undefined)
          ) {
            return (
              <span className="font-medium">
                {formatNumber(report.boughtMiles || 0)}
              </span>
            );
          }
          return <span className="text-muted-foreground">-</span>;
        },
      },
      {
        accessorKey: "soldMiles",
        header: ({ column }) => (
          <SortableHeader column={column}>Sold Miles</SortableHeader>
        ),
        cell: ({ row }) => {
          const report = row.original;
          if (
            (report.type === ReportType.PRODUCT &&
              "soldMiles" in report &&
              report.soldMiles !== undefined) ||
            (report.type === ReportType.MONTHLY &&
              "soldMiles" in report &&
              report.soldMiles !== undefined)
          ) {
            return (
              <span className="font-medium">
                {formatNumber(report.soldMiles || 0)}
              </span>
            );
          }
          return <span className="text-muted-foreground">-</span>;
        },
      },
      {
        accessorKey: "uniqueProducts",
        header: ({ column }) => (
          <SortableHeader column={column}>Unique Products</SortableHeader>
        ),
        cell: ({ row }) => {
          const report = row.original;
          if (
            report.type === ReportType.MONTHLY &&
            "uniqueProducts" in report
          ) {
            return (
              <span className="font-medium">
                {formatNumber(report.uniqueProducts || 0)}
              </span>
            );
          }
          return <span className="text-muted-foreground">-</span>;
        },
      },
      {
        accessorKey: "uniqueCustomers",
        header: ({ column }) => (
          <SortableHeader column={column}>Unique Contacts</SortableHeader>
        ),
        cell: ({ row }) => {
          const report = row.original;
          if (
            report.type === ReportType.MONTHLY &&
            "uniqueCustomers" in report
          ) {
            return (
              <span className="font-medium">
                {formatNumber(report.uniqueCustomers || 0)}
              </span>
            );
          }
          return <span className="text-muted-foreground">-</span>;
        },
      },
      {
        accessorKey: "topProductName",
        header: ({ column }) => (
          <SortableHeader column={column}>Top Product</SortableHeader>
        ),
        cell: ({ row }) => {
          const report = row.original;
          if (
            report.type === ReportType.MONTHLY &&
            "topProductName" in report &&
            report.topProductName
          ) {
            return (
              <div className="flex items-center justify-start gap-2">
                <Package className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">{report.topProductName}</span>
              </div>
            );
          }
          return <span className="text-muted-foreground">-</span>;
        },
      },
      {
        accessorKey: "topProductRevenue",
        header: ({ column }) => (
          <SortableHeader column={column}>Top Product Revenue</SortableHeader>
        ),
        cell: ({ row }) => {
          const report = row.original;
          if (
            report.type === ReportType.MONTHLY &&
            "topProductRevenue" in report &&
            report.topProductRevenue !== undefined
          ) {
            return (
              <span className="font-semibold text-green-600">
                {formatCurrency(report.topProductRevenue || 0)}
              </span>
            );
          }
          return <span className="text-muted-foreground">-</span>;
        },
      },
      {
        accessorKey: "dataAsOf",
        header: ({ column }) => (
          <SortableHeader column={column}>Data As Of</SortableHeader>
        ),
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground">
            {safeFormatDate(row.original.dataAsOf, "MMM dd, yyyy HH:mm")}
          </span>
        ),
      },
    ],
    [],
  );

  const table = useReactTable({
    data: reports,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    state: {
      sorting,
      columnVisibility,
    },
  });

  // Calculate column totals and averages
  const columnTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    const counts: Record<string, number> = {};

    reports.forEach((report) => {
      // Sum numeric columns
      if (report.totalRevenue !== undefined)
        totals.totalRevenue = (totals.totalRevenue || 0) + report.totalRevenue;
      if (report.averageDealValue !== undefined) {
        totals.averageDealValue =
          (totals.averageDealValue || 0) + report.averageDealValue;
        counts.averageDealValue = (counts.averageDealValue || 0) + 1;
      }
      if (report.wonDeals !== undefined)
        totals.wonDeals = (totals.wonDeals || 0) + report.wonDeals;
      if (report.lostDeals !== undefined)
        totals.lostDeals = (totals.lostDeals || 0) + report.lostDeals;
      if (report.totalDeals !== undefined)
        totals.totalDeals = (totals.totalDeals || 0) + report.totalDeals;
      if (report.numberOfPendingDeals !== undefined)
        totals.numberOfPendingDeals =
          (totals.numberOfPendingDeals || 0) + report.numberOfPendingDeals;
      if (report.totalQuantity !== undefined)
        totals.totalQuantity = (totals.totalQuantity || 0) + report.totalQuantity;
      if (report.lostRevenue !== undefined)
        totals.lostRevenue = (totals.lostRevenue || 0) + report.lostRevenue;
      if (report.potentialRevenue !== undefined)
        totals.potentialRevenue =
          (totals.potentialRevenue || 0) + report.potentialRevenue;

      // Average percentage columns
      if (report.winRate !== undefined) {
        totals.winRate = (totals.winRate || 0) + report.winRate;
        counts.winRate = (counts.winRate || 0) + 1;
      }
      if (report.pendingPercentageOfDeals !== undefined) {
        totals.pendingPercentageOfDeals =
          (totals.pendingPercentageOfDeals || 0) +
          report.pendingPercentageOfDeals;
        counts.pendingPercentageOfDeals =
          (counts.pendingPercentageOfDeals || 0) + 1;
      }
      if (report.wonPercentageOfTotal !== undefined) {
        totals.wonPercentageOfTotal =
          (totals.wonPercentageOfTotal || 0) +
          report.wonPercentageOfTotal;
        counts.wonPercentageOfTotal =
          (counts.wonPercentageOfTotal || 0) + 1;
      }
      if (report.lostPercentageOfTotal !== undefined) {
        totals.lostPercentageOfTotal =
          (totals.lostPercentageOfTotal || 0) +
          report.lostPercentageOfTotal;
        counts.lostPercentageOfTotal =
          (counts.lostPercentageOfTotal || 0) + 1;
      }
      if (report.rejectionRate !== undefined) {
        totals.rejectionRate =
          (totals.rejectionRate || 0) + report.rejectionRate;
        counts.rejectionRate = (counts.rejectionRate || 0) + 1;
      }

      // For product and monthly reports
      if ("boughtMiles" in report && report.boughtMiles !== undefined)
        totals.boughtMiles = (totals.boughtMiles || 0) + report.boughtMiles;
      if ("soldMiles" in report && report.soldMiles !== undefined)
        totals.soldMiles = (totals.soldMiles || 0) + report.soldMiles;
      if ("uniqueProducts" in report && report.uniqueProducts !== undefined)
        totals.uniqueProducts =
          (totals.uniqueProducts || 0) + report.uniqueProducts;
      if ("uniqueCustomers" in report && report.uniqueCustomers !== undefined)
        totals.uniqueCustomers =
          (totals.uniqueCustomers || 0) + report.uniqueCustomers;
      if ("topProductRevenue" in report && report.topProductRevenue !== undefined)
        totals.topProductRevenue =
          (totals.topProductRevenue || 0) + report.topProductRevenue;
    });

    // Calculate total deals if not already set
    if (!totals.totalDeals) {
      totals.totalDeals = (totals.wonDeals || 0) + (totals.lostDeals || 0) + (totals.numberOfPendingDeals || 0);
    }

    // Calculate averages for percentage columns
    if (counts.winRate) totals.winRate = totals.winRate / counts.winRate;
    if (counts.averageDealValue)
      totals.averageDealValue = totals.averageDealValue / counts.averageDealValue;
    if (counts.pendingPercentageOfDeals)
      totals.pendingPercentageOfDeals =
        totals.pendingPercentageOfDeals / counts.pendingPercentageOfDeals;
    if (counts.wonPercentageOfTotal)
      totals.wonPercentageOfTotal =
        totals.wonPercentageOfTotal / counts.wonPercentageOfTotal;
    if (counts.lostPercentageOfTotal)
      totals.lostPercentageOfTotal =
        totals.lostPercentageOfTotal / counts.lostPercentageOfTotal;
    if (counts.rejectionRate)
      totals.rejectionRate = totals.rejectionRate / counts.rejectionRate;

    return totals;
  }, [reports]);

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (reports.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Reports</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center h-32 text-center">
            <BarChart3 className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No reports found</p>
            <p className="text-sm text-muted-foreground mt-1">
              Try adjusting your filters
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-2 w-full">
      <div className="flex w-full justify-end">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="gap-2">
              <Settings2 className="h-4 w-4" />
              Columns
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            {table
              .getAllColumns()
              .filter((column) => {
                if (!column.getCanHide()) return false;

                // Define monthly-specific columns
                const monthlyColumns = [
                  "period",
                  "uniqueProducts",
                  "uniqueCustomers",
                  "topProductName",
                  "topProductRevenue",
                ];

                // Filter columns based on selectedReportType
                if (selectedReportType === ReportType.MONTHLY) {
                  // Hide product-specific columns for monthly reports
                  return column.id !== "productName";
                } else if (selectedReportType === ReportType.PRODUCT) {
                  // Hide monthly-specific columns for product reports
                  return !monthlyColumns.includes(column.id);
                }

                // Show all columns when selectedReportType is "all"
                return true;
              })
              .map((column) => {
                return (
                  <DropdownMenuCheckboxItem
                    key={column.id}
                    className="capitalize"
                    checked={column.getIsVisible()}
                    onCheckedChange={(value) =>
                      column.toggleVisibility(!!value)
                    }
                  >
                    {column.id
                      .replace(/([A-Z])/g, " $1")
                      .replace(/^./, (str) => str.toUpperCase())}
                  </DropdownMenuCheckboxItem>
                );
              })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="rounded-md border overflow-hidden overflow-x-auto relative">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="border-b">
                {headerGroup.headers.map((header, colIdx) => (
                  <TableHead
                    key={header.id}
                    className={`relative group whitespace-nowrap ${isNumericColumn(header.column.id) ? "text-right" : "text-left"} border-r last:border-r-0 ${colIdx === 0 ? "xl:sticky xl:left-0 xl:z-30 xl:bg-background xl:after:absolute xl:after:content-[''] xl:after:inset-y-0 xl:after:right-0 xl:after:w-px xl:after:bg-border" : ""}`}
                  >
                    {header.isPlaceholder ? null : (
                      <div
                        className={`flex items-center ${isNumericColumn(header.column.id) ? "justify-end" : "justify-start"}`}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                      </div>
                    )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => {
                const report = row.original;
                const sourceDealIds = "sourceDealIds" in report ? report.sourceDealIds : undefined;
                const isClickable = sourceDealIds && sourceDealIds.length > 0;
                
                const handleRowClick = () => {
                  if (isClickable) {
                    const reportName = 
                      report.type === ReportType.PRODUCT && "productName" in report
                        ? report.productName
                        : report.type === ReportType.MONTHLY && "monthKey" in report
                        ? `${report.monthKey} Monthly Report`
                        : "Report";
                    
                    navigate(`/reports/deals-details`, {
                      state: {
                        sourceDealIds,
                        reportType: report.type,
                        reportName,
                      },
                    });
                  }
                };

                return (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    className={`group odd:bg-muted/30 hover:bg-muted/50 border-b transition-colors duration-200 ${
                      isClickable ? "cursor-pointer" : ""
                    }`}
                    onClick={handleRowClick}
                  >
                  {row.getVisibleCells().map((cell, colIdx) => (
                    <TableCell
                      key={cell.id}
                      className={`whitespace-nowrap ${isNumericColumn(cell.column.id) ? "text-right" : "text-left"} border-r last:border-r-0 ${colIdx === 0 ? "xl:sticky xl:left-0 xl:z-20 xl:bg-background transition-colors duration-200 xl:before:absolute xl:before:content-[''] xl:before:inset-0 xl:before:pointer-events-none xl:before:transition-colors xl:before:duration-200 group-odd:xl:before:bg-muted/30 group-hover:xl:before:bg-muted/50 xl:before:z-0 xl:after:absolute xl:after:content-[''] xl:after:inset-y-0 xl:after:right-0 xl:after:w-px xl:after:bg-border xl:after:z-10" : ""}`}
                    >
                      {colIdx === 0 ? (
                        <div className="relative xl:z-10">
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </div>
                      ) : (
                        flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )
                      )}
                    </TableCell>
                  ))}
                  </TableRow>
                );
              })
            ) : (
              <TableRow className="border-b">
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          <TableFooter>
            <TableRow className="group bg-muted/30 hover:bg-muted/50 border-t-2 border-b transition-colors duration-200">
              {table.getVisibleFlatColumns().map((column, colIdx) => {
                const columnId = column.id;
                let cellContent: React.ReactNode = null;

                // First column shows "Total"
                if (colIdx === 0) {
                  cellContent = (
                    <span className="font-semibold">Total</span>
                  );
                } else if (columnId === "totalRevenue" && columnTotals.totalRevenue !== undefined) {
                  cellContent = (
                    <div className="flex items-center justify-end gap-2">
                      <span className="font-semibold text-green-600">
                        {formatCurrency(columnTotals.totalRevenue)}
                      </span>
                    </div>
                  );
                } else if (columnId === "averageDealValue" && columnTotals.averageDealValue !== undefined) {
                  cellContent = (
                    <div className="flex items-center justify-end gap-2">
                      <span className="font-semibold text-blue-600">
                        {formatCurrency(columnTotals.averageDealValue)}
                      </span>
                    </div>
                  );
                } else if (columnId === "totalDeals" && columnTotals.totalDeals !== undefined) {
                  cellContent = (
                    <span className="font-medium">{formatNumber(columnTotals.totalDeals)}</span>
                  );
                } else if (columnId === "wonDeals" && columnTotals.wonDeals !== undefined) {
                  cellContent = (
                    <div className="flex items-center justify-end gap-2">
                      <span className="font-medium text-green-600">
                        {formatNumber(columnTotals.wonDeals)}
                      </span>
                    </div>
                  );
                } else if (columnId === "lostDeals" && columnTotals.lostDeals !== undefined) {
                  cellContent = (
                    <div className="flex items-center justify-end gap-2">
                      <span className="font-medium text-red-600">
                        {formatNumber(columnTotals.lostDeals)}
                      </span>
                    </div>
                  );
                } else if (columnId === "numberOfPendingDeals" && columnTotals.numberOfPendingDeals !== undefined) {
                  cellContent = (
                    <div className="flex items-center justify-end gap-2">
                      <span className="font-medium text-yellow-600">
                        {formatNumber(columnTotals.numberOfPendingDeals)}
                      </span>
                    </div>
                  );
                } else if (columnId === "totalQuantity" && columnTotals.totalQuantity !== undefined) {
                  cellContent = (
                    <span className="font-medium">
                      {formatNumber(columnTotals.totalQuantity)}
                    </span>
                  );
                } else if (columnId === "lostRevenue" && columnTotals.lostRevenue !== undefined) {
                  cellContent = (
                    <div className="flex items-center justify-end gap-2">
                      <span className="font-semibold text-red-600">
                        {formatCurrency(columnTotals.lostRevenue)}
                      </span>
                    </div>
                  );
                } else if (columnId === "potentialRevenue" && columnTotals.potentialRevenue !== undefined) {
                  cellContent = (
                    <div className="flex items-center justify-end gap-2">
                      <span className="font-semibold text-purple-600">
                        {formatCurrency(columnTotals.potentialRevenue)}
                      </span>
                    </div>
                  );
                } else if (columnId === "boughtMiles" && columnTotals.boughtMiles !== undefined) {
                  cellContent = (
                    <span className="font-medium">
                      {formatNumber(columnTotals.boughtMiles)}
                    </span>
                  );
                } else if (columnId === "soldMiles" && columnTotals.soldMiles !== undefined) {
                  cellContent = (
                    <span className="font-medium">
                      {formatNumber(columnTotals.soldMiles)}
                    </span>
                  );
                } else if (columnId === "uniqueProducts" && columnTotals.uniqueProducts !== undefined) {
                  cellContent = (
                    <span className="font-medium">
                      {formatNumber(columnTotals.uniqueProducts)}
                    </span>
                  );
                } else if (columnId === "uniqueCustomers" && columnTotals.uniqueCustomers !== undefined) {
                  cellContent = (
                    <span className="font-medium">
                      {formatNumber(columnTotals.uniqueCustomers)}
                    </span>
                  );
                } else if (columnId === "topProductRevenue" && columnTotals.topProductRevenue !== undefined) {
                  cellContent = (
                    <span className="font-semibold text-green-600">
                      {formatCurrency(columnTotals.topProductRevenue)}
                    </span>
                  );
                }

                return (
                  <TableCell
                    key={columnId}
                    className={`whitespace-nowrap ${isNumericColumn(columnId) ? "text-right" : "text-left"} border-r last:border-r-0 ${colIdx === 0 ? "xl:sticky xl:left-0 xl:z-20 xl:bg-background transition-colors duration-200 xl:before:absolute xl:before:content-[''] xl:before:inset-0 xl:before:pointer-events-none xl:before:transition-colors xl:before:duration-200 xl:before:bg-muted/30 group-hover:xl:before:bg-muted/50 xl:before:z-0 xl:after:absolute xl:after:content-[''] xl:after:inset-y-0 xl:after:right-0 xl:after:w-px xl:after:bg-border xl:after:z-10" : ""}`}
                  >
                    {colIdx === 0 ? (
                      <div className="relative xl:z-10">{cellContent}</div>
                    ) : (
                      cellContent
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableFooter>
        </Table>
      </div>
    </div>
  );
}
